import { useContext, useMemo } from 'react'
import {
  buildPanelCheckItems,
} from 'components/Cruises/SearchPage/Filters/Inputs/CruiseExtraFiltersInputs'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'

import { trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { CRUISE_TYPE_FILTER_FACETS } from 'constants/cruise'
import { type IntlShape, useIntl } from 'react-intl'

type CruiseTypeFacet = {
  count?: number
  label: string
  slug: string
}

interface Props {
  cruiseTypes?: Set<string>
  onChange: (value: any) => void
  cruiseTypeFacets: Array<App.CruiseSearchFacetV2Item>
}

function getCruiseTypeLabel(intl: IntlShape, cruiseType: string): string {
  switch (cruiseType.toLowerCase()) {
    case 'ocean':
      return intl.formatMessage({
        id: 'OrBf-MAwgf0EKpApq4Gq',
        defaultMessage: 'Ocean',
        description: 'Cruise type filter option for ocean cruises in cruise search filters',
      })
    case 'river':
      return intl.formatMessage({
        id: '3orb5th_D1qt93ZO2-V0',
        defaultMessage: 'River',
        description: 'Cruise type filter option for river cruises in cruise search filters',
      })
    case 'expedition':
      return intl.formatMessage({
        id: '9hyNrQkqNqCXy36Kw_3y',
        defaultMessage: 'Expedition',
        description: 'Cruise type filter option for expedition cruises in cruise search filters',
      })
    case 'luxury':
      return intl.formatMessage({
        id: '8MILIjmh4NVS07fYqLPK',
        defaultMessage: 'Luxury',
        description: 'Cruise type filter option for luxury cruises in cruise search filters',
      })
    default:
      return cruiseType
  }
}

function CruiseCruiseTypeFilter({
  cruiseTypes,
  onChange,
  cruiseTypeFacets,
}: Props) {
  const intl = useIntl()
  const globalSearchState = useContext(GlobalSearchStateContext)

  const { cruiseTypeItems, hasCruiseTypes } = useMemo(() => {
    const cruiseTypeFacetSlugs = new Set(cruiseTypeFacets.map(facet => facet.slug.toLowerCase()))
    const cruiseTypeFilterFacets = CRUISE_TYPE_FILTER_FACETS.filter(
      facet => !cruiseTypeFacetSlugs.has(facet.slug.toLowerCase()),
    )
    const combinedCruiseTypeFacets: Array<CruiseTypeFacet> = [...cruiseTypeFacets, ...cruiseTypeFilterFacets]

    return {
      cruiseTypeItems: buildPanelCheckItems(combinedCruiseTypeFacets.map(({ count: _count, ...facet }) => ({
        ...facet,
        label: getCruiseTypeLabel(intl, facet.label),
      }))),
      hasCruiseTypes: combinedCruiseTypeFacets.some(facet => facet.count == null || facet.count > 0),
    }
  }, [cruiseTypeFacets, intl])

  if (!hasCruiseTypes) {
    return null
  }

  return <div data-testid="cruise-type-filter">
    <FilterPanelCheckboxGroup
      name="cruiseTypes"
      items={cruiseTypeItems}
      onChange={(e) => {
        trackEvent(searchEventWithContext({
          searchEvent: { type: 'filters' },
          sortContext: { value: 'recommended' },
          searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
          filterContexts: [{
            label: e.target.value,
            type: 'cruise_type',
            value: e.target.checked ? 'selected' : 'unselected',
          }],
        }))

        onChange(e)
      }}
      defaultSelected={cruiseTypes}
    />
  </div>
}

export default CruiseCruiseTypeFilter
